import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, useIntl } from 'react-intl';
import classNames from 'classnames';

import ZendeskHelp from '@/shared/components/ZendeskHelp';

import { showWhenPermission } from '@/shared/components/security';
import { useStateService } from '@/shared/services/StateService';
import TaskTagOverview from './TaskTagOverview';
import RelationTagOverview from './RelationTagOverview';
import DocumentTagConfiguration from './DocumentTagConfiguration';
import InteractionTagOverview from './InteractionTagOverview';
import messages from './tagSettingsMessages';
import RelationGroupOverview from './RelationGroupOverview';
import EmployeeTagOverview from './EmployeeTagOverview';

const routeNames = [
  'DocumentTags',
  'DocumentTypes',
  'Task',
  'Relation',
  'Interaction',
  'RelationGroups',
  'Employee',
];

const getInitialTab = tab => {
  if (!tab) return 3;
  if (typeof tab === 'string') {
    const idx = routeNames.findIndex(x => x.toLowerCase() === tab.toLowerCase());
    if (idx >= 0) return idx;
  }
  if (typeof tab === 'number' && tab >= 0 && tab < routeNames.length) {
    return tab;
  }

  return 3;
};

const TagSettingsContainer = ({ tab: initialTab }) => {
  const [tab, setTab] = useState(getInitialTab(initialTab));
  const intl = useIntl();
  const stateService = useStateService();
  const changeTab = useCallback(
    t => {
      setTab(t);
      stateService.gotoState('.', { tab: routeNames[t] });
    },
    [stateService],
  );

  return (
    <>
      <ul className="nav nav-tabs nav-tabs-heading" role="tablist">
        <li
          role="presentation"
          className={classNames('tab', { active: tab === 3 })}
          onClick={() => changeTab(3)}
        >
          <span>
            <FormattedMessage {...messages.RelationTags} /> <ZendeskHelp article="8055880173084" />
          </span>
        </li>
        <li
          role="presentation"
          className={classNames('tab', { active: tab === 5 })}
          onClick={() => changeTab(5)}
        >
          <span>
            <FormattedMessage {...messages.RelationGroups} />{' '}
            <ZendeskHelp article="15870358904988" />
          </span>
        </li>
        <li
          role="presentation"
          className={classNames('tab', { active: tab === 4 })}
          onClick={() => changeTab(4)}
        >
          <span>
            <FormattedMessage {...messages.InteractionTags} />{' '}
            <ZendeskHelp article="8399413400092" />
          </span>
        </li>
        <li
          role="presentation"
          className={classNames('tab', { active: tab === 2 })}
          onClick={() => changeTab(2)}
        >
          <span>
            <FormattedMessage {...messages.TaskTags} /> <ZendeskHelp article="8055825845148" />
          </span>
        </li>
        <li
          role="presentation"
          className={classNames('tab', { active: tab === 0 })}
          onClick={() => changeTab(0)}
        >
          <span>
            <FormattedMessage {...messages.DocumentTags} /> <ZendeskHelp article="5434863900050" />
          </span>
        </li>
        <li
          role="presentation"
          className={classNames('tab', { active: tab === 1 })}
          onClick={() => changeTab(1)}
        >
          <span>
            <FormattedMessage {...messages.DocumentTypes} /> <ZendeskHelp article="5434863900050" />
          </span>
        </li>
        <li
          role="presentation"
          className={classNames('tab', { active: tab === 6 })}
          onClick={() => changeTab(6)}
        >
          <span>
            <FormattedMessage {...messages.EmployeeTags} /> <ZendeskHelp article="17480569123228" />
          </span>
        </li>
      </ul>
      <div className="info-block-tab-panel panel">
        {tab === 0 || tab === 1 ? (
          <DocumentTagConfiguration intl={intl} stateService={stateService} tab={tab} />
        ) : null}
        {tab === 2 && <TaskTagOverview intl={intl} stateService={stateService} />}
        {tab === 3 && <RelationTagOverview intl={intl} stateService={stateService} />}
        {tab === 4 && <InteractionTagOverview intl={intl} />}
        {tab === 5 && <RelationGroupOverview intl={intl} stateService={stateService} />}
        {tab === 6 && <EmployeeTagOverview intl={intl} stateService={stateService} />}
      </div>
    </>
  );
};
TagSettingsContainer.propTypes = {
  tab: PropTypes.oneOfType([PropTypes.number, PropTypes.string, PropTypes.any]),
};
TagSettingsContainer.defaultProps = {
  tab: undefined,
};
export default showWhenPermission('Tags', 'read')(TagSettingsContainer);
